// src/pages/historial.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Chip, TextField, Button, Select, MenuItem, Avatar, Grid,
  Card, CardContent, CircularProgress, Alert, FormControl, InputLabel, Paper,
  Divider, useTheme, Accordion, AccordionSummary, AccordionDetails, Badge,
  Tooltip, Skeleton, IconButton, Stack
} from '@mui/material';
import {
  FaNotesMedical, FaUserCircle, FaSearch, FaCalendarAlt, FaUserMd,
  FaClinicMedical, FaProcedures, FaIdCard, FaVenusMars, FaBirthdayCake,
  FaChevronDown, FaRegCalendarCheck, FaCoins, FaGlobeAmericas, FaTint, FaCopy
} from 'react-icons/fa';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { sidebarColors } from '../styles/sidebarTheme';
import axiosInstance from "../api/axiosInstance";

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  margin: '0 auto',
  border: `3px solid ${sidebarColors.primaryMain}`,
  boxShadow: theme.shadows[3],
  '& img': { objectFit: 'contain', width: '100%', height: '100%' },
  [theme.breakpoints.down('sm')]: { width: 88, height: 88 }
}));

const TimelineDot = styled('div')(({ theme }) => ({
  width: 16, height: 16, borderRadius: '50%',
  backgroundColor: sidebarColors.primaryMain, border: `3px solid #fff`,
  position: 'absolute', left: -8, top: 20, zIndex: 2,
  [theme.breakpoints.down('sm')]: { width: 12, height: 12, left: -6, top: 18 }
}));

const TimelineConnector = styled('div')(({ theme }) => ({
  width: 2, backgroundColor: sidebarColors.borderSoft,
  position: 'absolute', left: 0, top: 36, bottom: 0, zIndex: 1,
  [theme.breakpoints.down('sm')]: { top: 30, left: -2 }
}));

const ProcedureChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? sidebarColors.primarySoft : '#2b3942',
  color: theme.palette.mode === 'light' ? sidebarColors.primaryMain : sidebarColors.accent,
  fontWeight: 500, borderRadius: 6, margin: '2px'
}));

// NUEVO: Chips de Origen + Financiador
const MetaChips = ({ origen, financiador }) => {
  if (!origen && !financiador) return null;
  const isLocal = String(origen || '').toLowerCase().includes('local');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {origen && (
        <Chip
          size="small"
          label={isLocal ? 'ORIGEN: LOCAL' : `ORIGEN: ${String(origen).toUpperCase()}`}
          sx={{
            fontWeight: 700,
            color: isLocal ? '#056608' : '#0b3d91',
            backgroundColor: isLocal ? 'rgba(5,102,8,.08)' : 'rgba(11,61,145,.08)',
            border: `1px solid ${isLocal ? 'rgba(5,102,8,.25)' : 'rgba(11,61,145,.25)'}`
          }}
        />
      )}
      {financiador && (
        <Chip
          size="small"
          label={`FINANCIADOR: ${financiador}`}
          sx={{
            fontWeight: 700,
            color: '#7b1fa2',
            backgroundColor: 'rgba(123,31,162,0.08)',
            border: '1px solid rgba(123,31,162,0.25)'
          }}
        />
      )}
    </Box>
  );
};

const Historial = () => {
  const theme = useTheme();
  const [tipoDocumento, setTipoDocumento] = useState(localStorage.getItem('hist_tipoDoc') || '');
  const [numeroDocumento, setNumeroDocumento] = useState(localStorage.getItem('hist_nroDoc') || '');
  const [paciente, setPaciente] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [cargandoTipos, setCargandoTipos] = useState(true);
  const [error, setError] = useState(null);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [expanded, setExpanded] = useState(localStorage.getItem('hist_panel') || null);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  const token = localStorage.getItem('token');
  const nombreUsuario = localStorage.getItem('nombre');

  const firstAccordionRef = useRef(null);

  const parsearFecha = (fechaString) => {
    if (!fechaString) return null;
    const partes = fechaString.split('/');
    if (partes.length === 3) {
      const [dia, mes, anio] = partes;
      return new Date(parseInt(anio), parseInt(mes) - 1, parseInt(dia));
    }
    const fechaIso = new Date(fechaString);
    if (!isNaN(fechaIso.getTime())) return fechaIso;
    return null;
  };

  const formatearFecha = (fechaString) => {
    const fecha = parsearFecha(fechaString);
    if (!fecha) return 'Fecha inválida';
    return fecha.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const ordenarPorFecha = (a, b) => {
    const fechaA = parsearFecha(a.fecha);
    const fechaB = parsearFecha(b.fecha);
    if (!fechaA) return 1;
    if (!fechaB) return -1;
    return fechaB - fechaA;
  };

  const handleChange = (panel) => (_e, isExpanded) => {
    const newPanel = isExpanded ? panel : null;
    setExpanded(newPanel);
    localStorage.setItem('hist_panel', newPanel ?? '');
    if (isExpanded && firstAccordionRef.current) {
      setTimeout(() => {
        firstAccordionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  };

  useEffect(() => {
    const obtenerTiposDocumento = async () => {
      try {
        const { data } = await axiosInstance.get('/tipos-documento');
        setTiposDocumento(data);
        if (!tipoDocumento && data.length > 0) setTipoDocumento(data[0].Id_Tipo_Documento);
      } catch (err) {
        console.error("Error al obtener tipos de documento:", err);
        setError("No se pudieron cargar los tipos de documento");
      } finally {
        setCargandoTipos(false);
      }
    };
    obtenerTiposDocumento();
  }, []);

  useEffect(() => {
    if (tipoDocumento) localStorage.setItem('hist_tipoDoc', String(tipoDocumento));
  }, [tipoDocumento]);

  useEffect(() => {
    localStorage.setItem('hist_nroDoc', numeroDocumento || '');
  }, [numeroDocumento]);




  const buscarPaciente = async () => {
    if (!numeroDocumento) { setError("Por favor ingrese un número de documento"); return; }
    if (!tipoDocumento) { setError("Seleccione el tipo de documento"); return; }

    // Verificación extra: si no hay token, no llames al backend protegido
    const tok = localStorage.getItem('token');
    if (!tok) {
      setError('Sesión expirada. Inicia sesión nuevamente.');
      window.location.href = '/login';
      return;
    }

    setCargando(true);
    setError(null);

    try {
      // 1) Chequeo ultra-rápido al nuevo endpoint

      const chk = await axiosInstance.get('/historial/existe', {
        params: { tipo_doc: tipoDocumento, nro_doc: numeroDocumento },
        timeout: 4000
      });
      if (!chk?.data?.has_attentions) {
        setPaciente(null);
        setExpanded(null);
        localStorage.removeItem('hist_panel');
        setError(`No se encontraron atenciones para el documento ${numeroDocumento}.`);
        return; // ← no seguimos con Persona/RENIEC
      }


      const respuestaLocal = await axiosInstance.post(
        '/buscar-persona',
        { tipo_doc: tipoDocumento, nro_doc: numeroDocumento },
        { headers: { usuario: nombreUsuario || 'usuario_autenticado' }, timeout: 8000 }
      );

      const origen = respuestaLocal.data.origen;
      const datosPaciente = respuestaLocal.data.datos;

      setPaciente({
        imagen: datosPaciente.foto,
        nombre_completo: `${datosPaciente.aPaterno} ${datosPaciente.aMaterno} ${datosPaciente.nombres}`,
        documento: datosPaciente.nroDocumento,
        sexo: datosPaciente.sexo,
        grupo_sanguineo: datosPaciente.grupo_sanguineo,
        fecha_nacimiento: datosPaciente.fecha_nacimiento,
        pais: datosPaciente.pais,
        departamento: datosPaciente.departamento,
        provincia: datosPaciente.provincia,
        atenciones: [],
        origen
      });

      await cargarHistorial(datosPaciente.nroDocumento);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || "Error al buscar el paciente";
      setError(errorMessage);
      setPaciente(null);
    } finally {
      setCargando(false);
    }
  };

  const cargarHistorial = async (documento) => {
    setCargandoHistorial(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Sesión expirada. Inicia sesión nuevamente.");
        return;
      }

      const { data } = await axiosInstance.post("/historial/", {
        tipo_doc: tipoDocumento,
        nro_doc: documento,
      });

      const atencionesProcesadas = (data.atenciones || [])
        .map((atencion) => ({
          ...atencion,
          fechaFormateada: formatearFecha(atencion.fecha),
          fechaOriginal: atencion.fecha,
          itemsAgrupados: atencion.items_por_tipo || {},
        }))
        .sort(ordenarPorFecha);

      setPaciente((prev) => ({ ...prev, atenciones: atencionesProcesadas }));
      const defaultPanel = atencionesProcesadas.length ? "panel0" : null;
      setExpanded(defaultPanel);
      localStorage.setItem("hist_panel", defaultPanel ?? "");
      setTimeout(() => {
        if (firstAccordionRef.current) {
          firstAccordionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 80);
    } catch (err) {
      console.error("Error al cargar historial:", err);
      if (err?.response?.status === 401) {
        setError("No autenticado. Inicia sesión nuevamente.");
      } else if (err?.response?.status === 403) {
        setError("No tienes permisos para ver este historial.");
      } else {
        setError("No se pudo cargar el historial de atenciones.");
      }
    } finally {
      setCargandoHistorial(false);
    }
  };

  const formatearGenero = (sexo) => (sexo === 'M' ? 'Masculino' : sexo === 'F' ? 'Femenino' : sexo);
  const obtenerDescripcionTipoDoc = (id) => {
    const tipo = tiposDocumento.find(t => String(t.Id_Tipo_Documento) === String(id));
    return tipo ? tipo.Abrev_Tipo_Doc : 'Desconocido';
  };
  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return 'N/A';
    const fechaNac = parsearFecha(fechaNacimiento);
    if (!fechaNac) return 'Fecha inválida';
    const hoy = new Date();
    let edad = hoy.getFullYear() - fechaNac.getFullYear();
    const mes = hoy.getMonth() - fechaNac.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) edad--;
    return edad;
  };

  const handleEnter = (e) => {
    if (e.key === 'Enter') buscarPaciente();
  };

  const limpiar = () => {
    setNumeroDocumento('');
    setPaciente(null);
    setError(null);
    setExpanded(null);
    localStorage.removeItem('hist_nroDoc');
    localStorage.removeItem('hist_panel');
  };

  const copiarDoc = async () => {
    try { await navigator.clipboard.writeText(paciente?.documento || ''); } catch { }
  };

  const financiadorPrincipal = useMemo(() => {
    return paciente?.atenciones?.[0]?.financiador || null;
  }, [paciente]);

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography
        variant="h4"
        sx={{
          mb: 3, display: 'flex', alignItems: 'center', gap: 2, fontWeight: 700,
          background: sidebarColors.titleGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          fontSize: { xs: 'clamp(1.25rem, 4.5vw, 1.75rem)', sm: '1.75rem' }
        }}
      >
        <FaNotesMedical /> Historial Clínico del Paciente
      </Typography>

      {/* Buscador */}
      <Card
        sx={{
          mb: 3, borderRadius: 3, boxShadow: theme.shadows[2],
          border: `1px solid ${sidebarColors.borderSoft}`, background: sidebarColors.bgGrad,
          transition: 'transform .3s, box-shadow .3s',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[6] }
        }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: sidebarColors.primaryMain, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Buscar Paciente
          </Typography>

          <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
            <Grid item xs={12} sm={4}>
              {cargandoTipos ? (
                <Skeleton variant="rounded" height={56} />
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="tipo-documento-label">Tipo de Documento</InputLabel>
                  <Select
                    labelId="tipo-documento-label"
                    label="Tipo de Documento"
                    value={tipoDocumento}
                    onChange={(e) => setTipoDocumento(e.target.value)}
                    disabled={cargando || tiposDocumento.length === 0}
                    sx={{ borderRadius: 2 }}
                  >
                    {tiposDocumento.map((tipo) => (
                      <MenuItem key={tipo.Id_Tipo_Documento} value={tipo.Id_Tipo_Documento}>
                        {tipo.Abrev_Tipo_Doc} - {tipo.Descripcion_Tipo_Documento}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Número de documento"
                value={numeroDocumento}
                onChange={(e) => setNumeroDocumento(e.target.value)}
                disabled={cargando}
                onKeyDown={handleEnter}
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: sidebarColors.borderSoft }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  fullWidth variant="contained" size="large"
                  startIcon={cargando ? <CircularProgress size={20} color="inherit" /> : <FaSearch />}
                  onClick={buscarPaciente}
                  disabled={!numeroDocumento || cargando || !tipoDocumento}
                  sx={{
                    borderRadius: 2, py: 1.3, textTransform: 'none', fontWeight: 700,
                    background: sidebarColors.titleGrad,
                  }}
                >
                  Buscar
                </Button>
                <Button
                  fullWidth variant="outlined" onClick={limpiar}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, borderColor: sidebarColors.borderSoft }}
                >
                  Limpiar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert
          severity={String(error).toLowerCase().includes('no encontrado') ? 'info' : 'error'}
          sx={{
            mb: 3, borderRadius: 2,
            borderLeft: `4px solid ${String(error).toLowerCase().includes('no encontrado') ? sidebarColors.primaryMain : sidebarColors.danger}`,
            backgroundColor: String(error).toLowerCase().includes('no encontrado') ? sidebarColors.primarySoft : 'rgba(255,86,86,0.06)'
          }}
          action={<Button color="inherit" size="small" onClick={() => setError(null)}>Ocultar</Button>}
        >
          {error}
        </Alert>
      )}

      {paciente && (
        <>
          {/* Header paciente */}
          <Card
            sx={{
              mb: 3, borderRadius: 3, boxShadow: theme.shadows[2],
              border: `1px solid ${sidebarColors.borderSoft}`, background: sidebarColors.bgGrad,
              transition: 'transform .3s, box-shadow .3s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[6] }
            }}
          >
            <CardContent>
              <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
                <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <StyledAvatar
                    src={paciente.imagen ? `data:image/jpeg;base64,${paciente.imagen}` : '/default-user.png'}
                    alt={paciente.nombre_completo}
                  >
                    {!paciente.imagen && <FaUserCircle size={56} color={sidebarColors.primaryMain} />}
                  </StyledAvatar>
                </Grid>

                <Grid item xs={12} md={9}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography
                      variant="h4"
                      gutterBottom
                      sx={{ fontWeight: 700, color: sidebarColors.primaryMain, mb: 0, fontSize: { xs: 'clamp(1.1rem, 4vw, 1.5rem)', sm: '1.75rem' } }}
                    >
                      {paciente.nombre_completo}
                    </Typography>
                    <MetaChips origen={paciente.origen} financiador={financiadorPrincipal} />
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2 }, backgroundColor: 'rgba(0, 180, 216, 0.04)',
                      borderRadius: 3, border: `1px solid ${sidebarColors.borderSoft}`
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: sidebarColors.primaryMain }}>
                      Datos personales
                    </Typography>

                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                          <FaIdCard color={sidebarColors.primaryMain} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="textSecondary">Documento</Typography>
                            <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                                {obtenerDescripcionTipoDoc(tipoDocumento)}: {paciente.documento}
                              </Typography>
                              <Tooltip title="Copiar"><IconButton size="small" onClick={copiarDoc}><FaCopy size={12} /></IconButton></Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <FaVenusMars color={sidebarColors.primaryMain} />
                          <Box>
                            <Typography variant="caption" color="textSecondary">Género</Typography>
                            <Typography variant="body2">{formatearGenero(paciente.sexo)}</Typography>
                          </Box>
                        </Box>
                      </Grid>

                      {paciente.grupo_sanguineo && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <FaTint color={sidebarColors.primaryMain} />
                            <Box>
                              <Typography variant="caption" color="textSecondary">Grupo Sanguíneo</Typography>
                              <Typography variant="body2">{paciente.grupo_sanguineo}</Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {paciente.fecha_nacimiento && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <FaBirthdayCake color={sidebarColors.primaryMain} />
                            <Box>
                              <Typography variant="caption" color="textSecondary">Edad</Typography>
                              <Typography variant="body2">{calcularEdad(paciente.fecha_nacimiento)} años</Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      <Grid item xs={12} sm={6} md={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <FaRegCalendarCheck color={sidebarColors.primaryMain} />
                          <Box>
                            <Typography variant="caption" color="textSecondary">Fecha de nacimiento</Typography>
                            <Typography variant="body2">{formatearFecha(paciente.fecha_nacimiento)}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2, borderColor: sidebarColors.borderSoft }} />

                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: sidebarColors.primaryMain }}>
                      Ubicación
                    </Typography>

                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <FaGlobeAmericas color={sidebarColors.primaryMain} />
                          <Box>
                            <Typography variant="caption" color="textSecondary">País</Typography>
                            <Typography variant="body2">{paciente.pais}</Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <FaClinicMedical color={sidebarColors.primaryLight} />
                          <Box>
                            <Typography variant="caption" color="textSecondary">Departamento / Provincia</Typography>
                            <Typography variant="body2">{paciente.departamento} / {paciente.provincia}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Typography
            variant="h5"
            sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, fontWeight: 700, color: sidebarColors.primaryMain, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
          >
            <FaRegCalendarCheck /> Historial de Atenciones ({paciente.atenciones.length})
          </Typography>

          {cargandoHistorial ? (
            <Box>{[...Array(3)].map((_, i) => (<Skeleton key={i} variant="rounded" height={92} sx={{ mb: 2 }} />))}</Box>
          ) : paciente.atenciones.length === 0 ? (
            <Alert
              severity="info"
              sx={{ borderRadius: 2, borderLeft: `4px solid ${sidebarColors.primaryMain}`, backgroundColor: sidebarColors.primarySoft }}
            >
              No se encontraron atenciones registradas para este paciente
            </Alert>
          ) : (
            <Box sx={{ position: 'relative', pl: { xs: 2, sm: 3 }, ml: { xs: 1, sm: 2 } }}>
              <TimelineConnector />
              {paciente.atenciones.map((atencion, index) => {
                const counts = Object.entries(atencion.itemsAgrupados || {}).reduce((acc, [tipo, arr]) => {
                  acc[tipo] = Array.isArray(arr) ? arr.length : 0;
                  return acc;
                }, {});
                return (
                  <Box key={index} sx={{ position: 'relative', mb: 3 }} ref={index === 0 ? firstAccordionRef : undefined}>
                    <TimelineDot />
                    <Accordion
                      expanded={expanded === `panel${index}`}
                      onChange={handleChange(`panel${index}`)}
                      sx={{
                        borderRadius: 2, boxShadow: theme.shadows[2],
                        border: `1px solid ${sidebarColors.borderSoft}`, '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<FaChevronDown color={sidebarColors.primaryMain} />}
                        sx={{
                          backgroundColor: expanded === `panel${index}` ? 'rgba(0,180,216,0.08)' : '#fff',
                          borderRadius: '8px 8px 0 0', '&:hover': { backgroundColor: 'rgba(0,180,216,0.06)' }
                        }}
                      >
                        <Grid container alignItems="center" spacing={{ xs: 1, sm: 2 }}>
                          <Grid item xs={12} sm={6}>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Badge
                                badgeContent={index + 1}
                                sx={{
                                  '& .MuiBadge-badge': {
                                    right: -10, top: -10, fontSize: '0.8rem', fontWeight: 'bold',
                                    background: sidebarColors.titleGrad, color: '#fff'
                                  }
                                }}
                              >
                                <FaCalendarAlt color={sidebarColors.primaryMain} />
                              </Badge>
                              <Typography variant="subtitle1" fontWeight={700}>{atencion.fechaFormateada}</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ minWidth: 0 }}>
                              <FaClinicMedical color={sidebarColors.primaryLight} />
                              <Typography variant="body2" noWrap sx={{ minWidth: 0, flexShrink: 1 }}>
                                {atencion.establecimiento}
                              </Typography>
                              {Object.keys(counts).slice(0, 3).map((k) => (
                                <Chip
                                  key={k}
                                  size="small"
                                  label={`${k}: ${counts[k]}`}
                                  sx={{ ml: 1, backgroundColor: 'rgba(0,180,216,.08)', border: `1px solid ${sidebarColors.borderSoft}` }}
                                />
                              ))}
                              {atencion.financiador && (
                                <Box display="flex" alignItems="center" gap={1} sx={{ ml: 1 }}>
                                  <FaCoins color="#2e7d32" />
                                  <Typography variant="body2" noWrap>
                                    <strong>Financiador:</strong>&nbsp;{atencion.financiador}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </AccordionSummary>

                      <AccordionDetails sx={{ pt: 0 }}>
                        <Divider sx={{ mb: 2, borderColor: sidebarColors.borderSoft }} />
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  display: 'flex', alignItems: 'center', gap: 1,
                                  color: sidebarColors.primaryMain, mb: 1, flexWrap: 'wrap', fontWeight: 600
                                }}
                              >
                                <FaUserMd />
                                <strong>UPS:</strong>&nbsp;{atencion.ups}
                                <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: sidebarColors.borderSoft, display: { xs: 'none', sm: 'block' } }} />
                                <strong>Atiende:</strong>&nbsp;{atencion.atiende}
                              </Typography>
                            </Box>

                            {Object.entries(atencion.itemsAgrupados || {}).map(([tipo, items]) => {
                              if (!Array.isArray(items) || items.length === 0) return null;
                              return (
                                <Box key={tipo} sx={{ mb: 2 }}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, color: sidebarColors.primaryMain, fontWeight: 700 }}
                                  >
                                    {tipo.includes('CIEX') ? <FaNotesMedical /> : <FaProcedures />}
                                    {tipo}
                                    <Chip size="small" label={items.length} sx={{ ml: 1 }} />
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', ml: 4, mt: 1 }}>
                                    {items.map((item, i) => (
                                      <Typography key={i} sx={{ mb: 1 }} variant="body2">
                                        <strong>{item.codigo}</strong>: {item.descripcion}
                                      </Typography>
                                    ))}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Historial;
